import { headerFlagKeys } from '@giffgaff/fe-design-system-content'

/**
 * Eppo feature flags resolved through the Client Tracking Context.
 *
 * Mirrors the shape fe-devices uses (`src/config/eppoFlags.ts`): names live here rather
 * than as string literals at the call site, so the middleware's `requestedFeatures`, the
 * loader's lookup and the MSW fixtures cannot drift apart on a typo. A flag the middleware
 * never requested silently resolves to "absent", which under a fail-closed gate reads as
 * "not entitled" rather than as an error.
 */

/**
 * Selects which homepage design the MFE renders (FAAS-783). It stopped being the access
 * gate in FAAS-909; EPPO_PORTAL_MFE_GATE took that job so this one is free to branch
 * between the Portal-parity page and the brand refresh.
 *
 * The name is the contract with Eppo and has to match the dashboard character for
 * character. It has moved twice on 15 Sep while the flag was recreated
 * (`brand-refresh-2026`, then `brand-refresh-26`, now this), so treat a redirect loop after
 * a flag change as a name mismatch first. A name nothing in Eppo answers to resolves as
 * "absent", which this fail-closed gate reads as "not entitled" rather than as an error, so
 * a typo here looks exactly like a correctly denied visitor.
 *
 * This used to claim "Eppo variants are strings, so the value is `'true'`, not `true`".
 * That is wrong and it was load-bearing: assignments-svc serialises a BOOLEAN variant as a
 * native JSON boolean, so `{"type":"BOOLEAN","value":true}` is what arrives. The toolkit's
 * `mapFeatures` tested only for the string and mapped every real boolean to `false`, which
 * is what redirected entitled members away from this page all day on 15 Sep. Fixed in
 * @fe-toolkit/utility; the note is kept rather than deleted because the belief it records
 * is the one that has to stop being repeated.
 */
export const EPPO_GIFFGAFF_BRAND_REFRESH_2026 = 'giffgaff-brand-refresh-2026'

/**
 * The comparison group for the brand refresh on the homepage, the same flag fe-devices reads
 * for /mobile-phones. Only matters to a visitor who already has EPPO_GIFFGAFF_BRAND_REFRESH_2026.
 *
 * Read the other way round from EPPO_BLACK_FRIDAY_2026_HOLDOUT below: ON means the visitor
 * gets the full brand refresh, OFF means they are held out and see the legacy homepage with
 * only the refresh chrome and OffersMasthead on it. Both fe-devices and fe-landing read it
 * this way, so don't "fix" the polarity here alone.
 *
 * It may be removed once the comparison is done, leaving the main flag to decide on its own.
 */
export const EPPO_SITE_REFRESH_2026_HOLDOUT = 'site-refresh-2026-holdout'

/**
 * This is a String flag in Eppo whose variations are the literal strings 'true' and
 * 'false', unlike EPPO_PORTAL_MFE_GATE which is a Boolean flag and arrives as a native
 * JSON boolean. assignments-svc echoes each flag's configured variation type, so the two
 * genuinely differ on the wire. The toolkit's isFeatureEnabled accepts either form.
 */

/**
 * Gates access to this MFE as a whole (FAAS-909). Portal reads the same flag server-side
 * before it decides whether to proxy `/` here, so both sides resolve it against the same
 * CTC context ID and always agree. That agreement is load-bearing: if Portal sent a
 * visitor this gate would then reject, the redirect back to Portal would be proxied
 * straight back here, and the visitor would loop.
 *
 * Kept separate from EPPO_GIFFGAFF_BRAND_REFRESH_2026 on purpose. This one answers "does
 * this visitor get the MFE at all", the other answers "which design do they see once they
 * are here". They were the same flag until FAAS-909 and the next reader will assume they
 * still are.
 */
export const EPPO_PORTAL_MFE_GATE = 'portal-mfe-gate'

/**
 * Portal's three promo toggles (FAAS-872).
 *
 * Portal resolves these from `configuration-svc` through
 * `App::configurationService()->isFeatureEnabled(...)`. The MFE cannot: that service has no
 * API gateway route for config reads, only a git webhook receiver. Eppo is the substitute.
 *
 * The names deliberately mirror Portal's own keys, kebab-cased to match the house style set
 * by EPPO_GIFFGAFF_BRAND_REFRESH_2026. That matters for more than tidiness. For a period
 * both Portal and this MFE serve /sim-only-deals to different audiences, and the two flag
 * systems have to be flipped together or the pages disagree on Black Friday morning.
 * Matching names make "who flips what" a sentence rather than a lookup table.
 *
 * None of these exist in Eppo yet. Creating them is a change to a third-party system that
 * decides what production renders, so it belongs with whoever owns the Eppo project. Until
 * they exist, an unknown flag resolves as absent, which reads as false, which is why the
 * loader ORs these with the date windows in promoFlags.ts rather than replacing them. See
 * the note there before deleting either half.
 */
/**
 * Black Friday 2026 is two flags, not one, and they do opposite things.
 *
 * `black-friday-2026` switches the campaign on. `black-friday-2026-holdout` is a true holdout:
 * resolving true means this visitor is EXCLUDED, so it is negated at the point of use. Both
 * are read across every Black Friday 2026 component, not just this page, so the keys have to
 * match whatever the other consumers use.
 */
export const EPPO_BLACK_FRIDAY_2026 = 'black-friday-2026'
export const EPPO_BLACK_FRIDAY_2026_HOLDOUT = 'black-friday-2026-holdout'
export const EPPO_H2_PROMOS_HOMEPAGE_BANNER = 'h2-promos-homepage-banner'

/**
 * The Black Friday keys the header and footer follow, from feds-content's headerFlagKeys, which
 * fe-common's chrome uses too. Not the same flags as EPPO_BLACK_FRIDAY_2026 above, which drives
 * the /sim-only-deals promos.
 *
 * Read here so the fallback chrome (src/components/fallbacks) can be decided on the server when
 * fe-common is down (fe-common's own decision, chromeFlags, needs the same remote), and so the
 * homepage's campaign masthead switches with the chrome: `black-friday-master` gates the brand
 * refresh OffersMasthead and the holdout's OfferBanner (resolveIsBlackFriday in layout.data.ts).
 */
export const EPPO_CHROME_BLACK_FRIDAY = headerFlagKeys.blackFriday
export const EPPO_CHROME_BLACK_FRIDAY_PREREG = headerFlagKeys.blackFridayPrereg
export const EPPO_ENABLE_MGM_PROMO = 'enable-mgm-promo'

/**
 * Flags resolved server-side, per request, by `createCtcFeaturesMiddlewareMJS` in
 * server/modern.server.ts. Anything needed by a loader has to be in this list.
 *
 * Server-side and not the client `useFeatureEnabled` hook, which is useState/useEffect
 * driven and resolves after mount. A promo banner resolved that way would visibly flip in
 * after paint; Portal resolves in PHP before any HTML exists and has no flash.
 *
 * Only add a flag something actually reads. Requesting one nothing reads costs a wider CTC
 * resolution on every request and reads as though a switch is wired when it is not. The four
 * promo entries below are read by the /sim-only-deals loader in this same change; the three
 * above are read by the root route.
 */
export const SERVER_EPPO_FLAGS = [
  EPPO_PORTAL_MFE_GATE,
  EPPO_GIFFGAFF_BRAND_REFRESH_2026,
  EPPO_SITE_REFRESH_2026_HOLDOUT,
  EPPO_BLACK_FRIDAY_2026,
  EPPO_BLACK_FRIDAY_2026_HOLDOUT,
  EPPO_H2_PROMOS_HOMEPAGE_BANNER,
  EPPO_ENABLE_MGM_PROMO,
  EPPO_CHROME_BLACK_FRIDAY,
  EPPO_CHROME_BLACK_FRIDAY_PREREG,
]
